import { AlertTriangle, ShieldAlert } from "lucide-react";

import { useAppSelector } from "@/store/hooks";

interface Props {
  checked: boolean;

  onCheckedChange: (checked: boolean) => void;

  assetSymbol?: string;

  networkName?: string;
}

const WithdrawalWarning = ({
  checked,
  onCheckedChange,
  assetSymbol,
  networkName,
}: Props) => {
  const { theme } = useAppSelector((state) => state.ui);

  return (
    <div
      className={`rounded-xl border p-5 ${
        theme === "dark"
          ? "border-yellow-500/20 bg-yellow-500/5"
          : "border-yellow-200 bg-yellow-50"
      }`}
    >
      <div className="flex items-center gap-3">
        <ShieldAlert className="w-6 h-6 text-yellow-500" />

        <div>
          <h3 className="font-semibold">Important Withdrawal Notice</h3>

          <p className="text-xs text-muted-foreground mt-1">
            Please carefully review the information below.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        <WarningItem>
          Ensure the destination wallet supports{" "}
          <strong>{assetSymbol ?? "this asset"}</strong>.
        </WarningItem>

        <WarningItem>
          Ensure you selected the correct blockchain network{" "}
          <strong>{networkName ?? ""}</strong>.
        </WarningItem>

        <WarningItem>
          Sending assets to an incorrect blockchain network may result in
          permanent loss of funds.
        </WarningItem>

        <WarningItem>
          Blockchain withdrawals cannot be cancelled or reversed once broadcast.
        </WarningItem>

        <WarningItem>
          Some exchanges require destination tags or memos for certain assets.
          Ensure all required information is provided before continuing.
        </WarningItem>
      </div>

      <label className="flex items-start gap-3 mt-6 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheckedChange(e.target.checked)}
          className="mt-1"
        />

        <span className="text-sm">
          I understand that blockchain withdrawals are irreversible and I accept
          full responsibility for ensuring that the destination address,
          blockchain network, memo/tag (if required), and asset are correct.
        </span>
      </label>
    </div>
  );
};

interface WarningItemProps {
  children: React.ReactNode;
}

function WarningItem({ children }: WarningItemProps) {
  return (
    <div className="flex items-start gap-3">
      <AlertTriangle className="w-4 h-4 text-yellow-500 mt-1 flex-shrink-0" />

      <p className="text-sm">{children}</p>
    </div>
  );
}

export default WithdrawalWarning;
